import { observer } from 'mobx-react-lite'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'

import {
	ConfigProvider,
	Form,
	Input,
	Card,
	Row,
	Col,
	Tag,
	Tooltip,
	Select,
	Space
} from 'antd'
import {
	WarningOutlined,
	InfoCircleOutlined,
	HistoryOutlined,
	EyeOutlined,
	SettingOutlined
} from '@ant-design/icons'
import { runInAction, toJS } from 'mobx'

import { API_BASE_URL, IS_NEW } from '../../../config'
import { useStores } from '../../../cls/stores/MobXProvider'

import { SubmitButton } from '../../../components/Form'
import { FormatedTimestamp } from '../../../components/Shared'
import ProgressInput from '../../../components/Form/ProgressInput'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import '../../../design/styles/patterns.css' // Create a CSS file to handle custom styles
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import {
	actSaveRecruitment,
	requireRecruitmentById
} from '../../../stores/recruitment/actions'
import FileUpload from '../../../components/Shared/FileUpload'
import Title from 'antd/es/typography/Title'
import { highestEducationOptions } from './RecruitmentApply'
import { useSmartTranslation } from '../../../cls/TranslationContext'

/**
 * @function RecruitmentEdit
 * @description Main component for creating or editing a recruitment entry.
 * Renders a form with fields such as name, contact, education, CV upload, and social links.
 * Supports both edit and view-only modes, and integrates with interval-based sync and tour features.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.viewOnly - Flag to render the form in view-only mode.
 */
const RecruitmentEdit = ({ viewOnly = false }) => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { itmId } = useParams()
	const { users, tourStore } = useStores()
	const { loggedInUserId, loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const _itm = requireRecruitmentById(itmId)

	const formIsDisabled = viewOnly

	const [form] = Form.useForm()

	const selfPermission = isAdmin || isSuperAdmin
	// and item not new
	if (!selfPermission && _itm.loaded !== IS_NEW) {
		viewOnly = true
	}

	// Function to generate initial form values
	/**
	 * @function generateInitialFormValues
	 * @description Returns the initial values to populate the recruitment form, using existing data from the store.
	 *
	 * @returns {Object} - Initial form field values.
	 */
	const generateInitialFormValues = () => ({
		name: _itm.name,
		email: _itm.email,
		phone: _itm.phone,
		highest_education: _itm.highest_education,
		cv: _itm.cv,
		linkedin: _itm.linkedin,
		facebook: _itm.facebook,
		website: _itm.website,
		progress: _itm.progress || 'pending',
		created_by: _itm.created_by || loggedInUserId
	})

	/**
	 * @function _doSave
	 * @description Handles form submission. Updates the recruitment item in the MobX store and persists it via API.
	 *
	 * @param {Object} vals - Form values submitted by the user.
	 */
	const _doSave = (vals) => {
		if (vals !== null) {
			runInAction(() => {
				_itm.setName(vals.name)
				_itm.setEmail(vals.email)
				_itm.setPhone(vals.phone)
				_itm.setHighestEducation(vals.highest_education)
				_itm.setCv(vals.cv)
				_itm.setLinkedin(vals.linkedin)
				_itm.setWebsite(vals.website)
				_itm.setFacebook(vals.facebook)
				_itm.setProgress(vals.progress)
				_itm.setCreatedBy(loggedInUserId)
			})

			actSaveRecruitment(itmId, toJS(_itm.full))
				.then(() => {
					navigate('/recruitments')
				})
				.catch(console.error)
		}
	}

	// Handle file upload change
	/**
	 * @function handleFileChange
	 * @description Updates the form value for the 'cv' field when file uploads are modified.
	 *
	 * @param {Array<string>} fileList - List of uploaded file identifiers.
	 */
	const handleFileChange = (fileList) => {
		// fileList is array of ids
		form.setFieldsValue({ cv: fileList })
	}

	const tourForm = getTourArrayByKeys('form', ['section', 'name'])

	/**
	 * Start tour:
	 * This function starts the tour for the report form.
	 * It includes the general form, subtasks, comment and recurrence fields.
	 */
	const startTour = () => {
		tourStore.startTour('RecruitmentEdit-tour', [
			!!!_itm.to_delete && {
				title: 'Sync on interval',
				description:
					'Enable or disable data synchronization on interval for the report. Any additions or modifications will be automatically updated even if working in multiple tabs or is updated by another user.',
				target: () => document.getElementById('target-interval-itm')
			},

			// General info
			...getTourArrayByKeys('info', ['name', 'modified']),

			// General form
			...getTourArrayByKeys('form', ['section'])
		])
	}

	/**
	 * @function startTourForm
	 * @description Starts the guided UI tour focused on the recruitment form fields.
	 */
	const startTourForm = () => {
		tourStore.startTour('RecruitmentEdit-tour', [
			// General form
			...tourForm,
			...getTourArrayByKeys('recruitmentForm', ['all'])
		])
	}

	console.log(
		_itm.cv,
		_itm.cv.map((cv) => ({
			key: cv,
			uid: cv,
			name: cv.split('.').pop().toUpperCase(),
			status: 'done',
			url: `${API_BASE_URL}/uploads/${cv}`
		}))
	)

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_recruitment') }}
				buttonProps={{
					onClick: startTour
				}}
				rowProps={{
					justify: 'space-between'
				}}
			>
				{!!_itm.to_delete && (
					<Tooltip
						title={
							<>
								{t('single.delete-on')}{' '}
								<FormatedTimestamp timestamp={_itm.to_delete} />
							</>
						}
					>
						<Tag color="red" icon={<WarningOutlined />}>
							{t('single.delete-marked')}
						</Tag>
					</Tooltip>
				)}
			</GeneralTourTitle>

			<Card
				title={
					<span id="target-info-name">
						{viewOnly ? (
							<Tooltip title={t('__view%_recruitment')}>
								<EyeOutlined id="target-mode" /> {_itm.name}
							</Tooltip>
						) : _itm.loaded === IS_NEW ? (
							t('__create-new%_recruitment')
						) : (
							<Tooltip title={t('__edit%_recruitment')}>
								<SettingOutlined /> {_itm.name}
							</Tooltip>
						)}{' '}
					</span>
				}
				style={{ width: '100%' }}
				extra={
					<div id="target-info-modified">
						<Tooltip title={t('single.last-updated')}>
							<FormatedTimestamp
								timestamp={_itm.modifiedTs}
								formatedAs="DD.MM.YYYY hh:mm"
							/>{' '}
							<HistoryOutlined />
						</Tooltip>
					</div>
				}
			>
				<ConfigProvider>
					<Form
						layout="vertical"
						form={form}
						name="report"
						initialValues={generateInitialFormValues()}
						onFinish={_doSave}
						className={viewOnly && 'view-only-styles'}
					>
						<Space style={{ width: '100%' }} direction="vertical">
							<GeneralTourTitle
								align="end"
								titleProps={{
									text: t('single.general'),
									h: Title,
									level: 5
								}}
								buttonProps={{
									onClick: startTourForm,
									disabled: false,
									style: { marginBottom: 4 }
								}}
							/>

							<Row id="target-form" gutter={[50, 50]}>
								<Col span={24}>
									<Row gutter={[20, 20]}>
										<Col
											xs={24}
											sm={24}
											md={12}
											lg={12}
											id="target-form-name"
										>
											<Form.Item
												label={t('single.name')}
												tooltip={t(
													'__enter.please%single.name'
												)}
												name="name"
												rules={[
													{
														required: true,
														message: t(
															'__provide.please%single.name'
														)
													}
												]}
											>
												<Input
													disabled={formIsDisabled}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} sm={24} md={12} lg={12}>
											<Row
												align="bottom"
												gutter={6}
												id="target-form-progress"
											>
												<Col id="target-form-progress">
													<Form.Item
														label={t(
															'single.progress'
														)}
														tooltip={{
															title: t(
																'single.curent-progress'
															),
															icon: (
																<InfoCircleOutlined />
															)
														}}
														name="progress"
														rules={[
															{
																required: true,
																message: t(
																	'single.progress-required'
																)
															}
														]}
													>
														<ProgressInput
															type="recruitment"
															viewOnly={viewOnly}
															disabled={
																formIsDisabled
															}
														/>
													</Form.Item>
												</Col>
											</Row>
										</Col>
									</Row>
									<Row gutter={[20, 20]}>
										<Col
											id="target-form-email"
											xs={24}
											sm={24}
											md={12}
											lg={12}
										>
											<Form.Item
												label="Email"
												tooltip={t(
													'single.reach.email'
												)}
												name="email"
												rules={[
													{
														required: true,
														message: t(
															'__provide.please%_email.address'
														)
													},
													{
														type: 'email',
														message: t(
															'single.invalid.email'
														)
													}
												]}
											>
												<Input
													disabled={formIsDisabled}
												/>
											</Form.Item>
										</Col>
										<Col
											id="target-form-phone"
											xs={24}
											sm={24}
											md={12}
											lg={12}
										>
											<Form.Item
												label={t('single.phone')}
												tooltip={t(
													'single.invalid.phone'
												)}
												name="phone"
												rules={[
													{
														required: true,
														message: t(
															'__provide.please%single.phone-number'
														)
													}
												]}
											>
												<Input
													disabled={formIsDisabled}
												/>
											</Form.Item>
										</Col>
									</Row>

									<Row gutter={[20, 20]}>
										<Col
											id="target-form-education"
											xs={24}
											sm={24}
											md={12}
											lg={12}
										>
											<Form.Item
												label={t(
													'single.highest-education'
												)}
												tooltip={t(
													'__select.please%single.highest-education'
												)}
												name="highest_education"
												rules={[
													{
														required: true,
														message: t(
															'__select.please%single.highest-education'
														)
													}
												]}
											>
												<Select
													disabled={formIsDisabled}
													options={
														highestEducationOptions
													}
												/>
											</Form.Item>
										</Col>
										<Col
											id="target-form-cv"
											xs={24}
											sm={24}
											md={12}
											lg={12}
										>
											<Form.Item
												label="CV"
												tooltip={t(
													'single.apply-job.upload-cv'
												)}
												name="cv"
												rules={[
													{
														required: true,
														message: t(
															'single.apply-job.upload-cv'
														)
													}
												]}
											>
												<FileUpload
													entity_table="recruitments"
													entity_uid={itmId}
													disabled={formIsDisabled}
													initialFileList={_itm.cv.map(
														(cv) => ({
															key: cv,
															uid: cv,
															name: cv
																.split('.')
																.pop()
																.toUpperCase(),
															status: 'done',
															url: `${API_BASE_URL}/uploads/${cv}`
														})
													)}
													onChange={handleFileChange}
												/>
											</Form.Item>
										</Col>
									</Row>
									<div id="target-form-linkedin">
										<Form.Item
											label="Linkedin URL"
											tooltip={t(
												'platform.linkedin.aplicant-url'
											)}
											name="linkedin"
										>
											<Input disabled={formIsDisabled} />
										</Form.Item>
									</div>
									<div id="target-form-facebook">
										<Form.Item
											label="Facebook URL"
											tooltip={t(
												'platform.facebook.aplicant-url'
											)}
											name="facebook"
										>
											<Input disabled={formIsDisabled} />
										</Form.Item>
									</div>
									<div id="target-form-website">
										<Form.Item
											label={t(
												'single.portfolio-website'
											)}
											tooltip={t(
												'single.provide-aplicant-url-website'
											)}
											name="website"
										>
											<Input disabled={formIsDisabled} />
										</Form.Item>
									</div>
								</Col>
							</Row>
							{
								// Show  cancel button only if editing resource
								!viewOnly && (
									<Row gutter={[10, 10]} align="bottom">
										<Col>
											<SubmitButton
												disabled={
													!selfPermission &&
													!users.canUser(
														t('single.write'),
														t(
															'_monthly-report.multiple'
														)
													)
												}
											>
												{_itm.loaded === IS_NEW
													? t('single.create')
													: t('single.update')}
											</SubmitButton>
										</Col>
										<Col>
											{/* <Button
												type="default"
												onClick={() => navigate(-1)}
											>
												{t('single.cancel')}
											</Button> */}
										</Col>
									</Row>
								)
							}
						</Space>
					</Form>
				</ConfigProvider>
			</Card>
		</GeneralContentWrapper>
	)
}
RecruitmentEdit.propTypes = {
	viewOnly: PropTypes.bool
}

export default observer(RecruitmentEdit)
